import { motion } from 'motion/react';
import { ShoppingBag } from 'lucide-react';
import { useLandingContent } from '../../lib/content';

interface Props {
  open: boolean;
  qty: number;
  onUpdateQuantity: (q: number) => void;
  onConfirm: () => void;
}

export function QuantityPromptModal({ open, qty, onUpdateQuantity, onConfirm }: Props) {
  const { modals } = useLandingContent('modals');
  const m = modals.quantityPrompt ?? {
    title: 'Select Quantity',
    body:
      "Choose how many boxes you'd like to order. Prices and availability will update automatically.",
    cta: 'Confirm Quantity',
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-[40px] p-8 md:p-12 max-w-lg w-full shadow-2xl space-y-8"
      >
        <div className="text-center space-y-4">
          <div className="w-20 h-20 bg-[#356d4e]/10 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-10 h-10 text-[#356d4e]" />
          </div>
          <h2 className="text-3xl font-black text-[#356d4e] uppercase tracking-tight">
            {m.title}
          </h2>
          <p className="text-gray-500 font-medium">{m.body}</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[25, 50, 100, 150, 250].map((q) => (
            <button
              key={q}
              onClick={() => {
                onUpdateQuantity(q);
                onConfirm();
              }}
              className={`py-4 rounded-2xl font-black text-lg transition-all ${
                qty === q
                  ? 'bg-[#356d4e] text-white shadow-xl scale-105'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {q}
            </button>
          ))}
          <div className="relative">
            <input
              type="number"
              value={qty}
              onChange={(e) => onUpdateQuantity(parseInt(e.target.value) || 0)}
              className="w-full h-full p-4 bg-gray-50 border border-gray-200 rounded-2xl font-black text-[#356d4e] outline-none focus:border-[#57c858] transition-all text-lg text-center"
              placeholder="Custom"
              min="1"
            />
          </div>
        </div>

        <button
          onClick={onConfirm}
          className="w-full py-5 bg-[#356d4e] text-white rounded-2xl font-black text-lg uppercase tracking-widest hover:brightness-110 transition-all shadow-xl"
        >
          {m.cta ?? 'Confirm Quantity'}
        </button>

        <p className="text-center text-xs font-bold text-gray-400">
          Prices and availability depend on number of boxes.
        </p>
      </motion.div>
    </div>
  );
}
